// src/components/lead/sections/DevConvoPanel.tsx
import { useMemo, useState } from "react";
import {
  cardMutedTextClass,
  cardSurfaceClass,
  cardTitleTextClass,
  compactInputClass,
  secondarySurfaceClass,
} from "@/components/common/surfaceClasses";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { answerKbQuestion } from "../../../lib/kb/answer";
import { enqueueKbGapEvent } from "../../../lib/training/feedbackQueue";
import { useSpeechDictation } from "../../../hooks/useSpeechDictation";
import { DevConvoDictationSection } from "./DevConvoDictationSection";
import type { Location } from "../../../types/location";

type SalespersonLike = { name: string } | null;

export function DevConvoPanel(props: {
  appVersion: string;
  salesperson: SalespersonLike;
  location: Location | null;
}) {
  const { appVersion, salesperson, location } = props;
  const scopedOrganizationId = useScopedOrganizationId();

  const salespersonFirstName = useMemo(() => {
    const name = (salesperson?.name || "").trim();
    if (!name) return undefined;
    return name.split(" ")[0];
  }, [salesperson?.name]);

  const [prompt, setPrompt] = useState("");
  const dictation = useSpeechDictation();

  const [assistantAnswer, setAssistantAnswer] = useState("");
  const [kbMeta, setKbMeta] = useState<
    { bestConfidence: number; sources: any[] } | null
  >(null);
  const [proposedAnswer, setProposedAnswer] = useState("");
  const [reason, setReason] = useState<
    "no_hits" | "low_confidence" | "thumbs_down" | "error"
  >("no_hits");

  const [extraTags, setExtraTags] = useState("dev"); // comma-separated
  const [savedToast, setSavedToast] = useState<string | null>(null);

  const onAsk = async (overridePrompt?: string) => {
    const q = (overridePrompt ?? prompt).trim();
    if (!q) return;

    try {
      const out = await answerKbQuestion({
        query: q,
        audience: "internal",               // DevConvoMode is manager-facing
        locationId: location?.id?.trim() ? location.id : null,   // optional filtering support
        organizationId: scopedOrganizationId,
        topK: 8,
        lane: "answers",
      });

      setAssistantAnswer(out.answer || "");
      setKbMeta({ bestConfidence: out.bestConfidence, sources: out.sources });

      // Optional: if KB says "no_answer", preselect a reason automatically
      if (out.mode === "no_answer") setReason("no_hits");
      else if (out.mode === "suggested_answer") setReason("low_confidence");
    } catch (err) {
      console.error("[kb] /api/kb/answer failed", err);
      setKbMeta(null);
      setReason("error");
      setAssistantAnswer(
        "KB unavailable right now. Please try again (see console for details)."
      );
    }
  };

  const confirmDictationAsk = () => {
    const text = (dictation.draft || "").trim();
    if (!text) return;

    if (dictation.isListening) dictation.stop();

    // 1) Fill prompt
    setPrompt(text);

    // 2) Auto-ask (don’t depend on React state timing)
    window.setTimeout(() => {
      void onAsk(text);
    }, 0);

    window.setTimeout(() => dictation.reset(), 0);
  };

  const onLogGap = () => {
    const tags = extraTags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    enqueueKbGapEvent({
      version: appVersion,
      query: prompt,
      reason,
      answerPreview: assistantAnswer || undefined,
      feedbackText: proposedAnswer || undefined,
      extraTags: tags.length ? tags : undefined,

      locationId: location?.id ?? undefined,
      locationName: location?.name ?? undefined,
      salespersonFirstName,
    });

    setSavedToast("Logged kb_gap event to queue.");
    setTimeout(() => setSavedToast(null), 2500);

    // Clear only the prompt by default; keep proposed answer around if you're iterating
    setPrompt("");
  };

  const canSubmit = prompt.trim().length > 0;

  return (
    <div className={`rounded-2xl p-4 space-y-3 ${cardSurfaceClass}`}>
      <div className="flex items-center justify-between">
        <h3 className={`text-lg font-semibold ${cardTitleTextClass}`}>Dev Convo Mode</h3>
        <span className={`text-xs ${cardMutedTextClass}`}>
          Logs kb_gap → webhook workflow email
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div className={`rounded-xl p-2 ${secondarySurfaceClass}`}>
          <div className={`text-xs mb-1 min-w-0 ${cardMutedTextClass}`}>Salesperson</div>
          <div className={`font-medium ${cardTitleTextClass}`}>{salesperson?.name || "—"}</div>
        </div>
        <div className={`rounded-xl p-2 ${secondarySurfaceClass}`}>
          <div className={`text-xs mb-1 min-w-0 ${cardMutedTextClass}`}>Location</div>
          <div className={`font-medium ${cardTitleTextClass}`}>{location?.name || "—"}</div>
        </div>
        <div className={`rounded-xl p-2 ${secondarySurfaceClass}`}>
          <div className={`text-xs mb-1 min-w-0 ${cardMutedTextClass}`}>Version</div>
          <div className={`font-medium ${cardTitleTextClass}`}>{appVersion}</div>
        </div>
      </div>

      <DevConvoDictationSection dictation={dictation} onConfirmAsk={confirmDictationAsk} />

      <div className="space-y-1">
        <label className="text-sm font-medium">User prompt (required)</label>
        <textarea
          className={`w-full rounded-xl p-2 text-sm ${compactInputClass}`}
          rows={3}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder='e.g., "Customer wants electric included and bigger than a 10x20 — what do I say?"'
        />
        <div className="text-xs text-gray-500 break-all">Prompt debug: {JSON.stringify(prompt)}</div>
      </div>

      <div className="space-y-1">
        <label className="text-sm font-medium">Buddy answer (optional)</label>
        <textarea
          className={`w-full rounded-xl p-2 text-sm ${compactInputClass}`}
          rows={3}
          value={assistantAnswer}
          onChange={(e) => setAssistantAnswer(e.target.value)}
          placeholder="Paste what Buddy answered (if applicable)."
        />

        {kbMeta && (
          <div className={`text-xs rounded-xl p-2 ${secondarySurfaceClass}`}>
            <div>Best confidence: {kbMeta.bestConfidence.toFixed(3)}</div>
            <div className="mt-1 space-y-1">
              {kbMeta.sources.slice(0, 3).map((s, idx) => (
                <div key={idx} className="truncate">
                  {s.title} — {s.score.toFixed(3)}{" "}
                  {s.sourceUrl ? (
                    <a
                      className="underline"
                      href={s.sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      source
                    </a>
                  ) : (
                    <span className="text-gray-400">(no link)</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="space-y-1">
        <label className="text-sm font-medium">Proposed answer / notes (optional)</label>
        <textarea
          className={`w-full rounded-xl p-2 text-sm ${compactInputClass}`}
          rows={3}
          value={proposedAnswer}
          onChange={(e) => setProposedAnswer(e.target.value)}
          placeholder="Type the answer you'd want Buddy to give, or notes for KB."
        />
      </div>


      <div className="flex flex-col md:flex-row gap-2 md:items-end">
        <div className="flex-1">
          <label className="text-sm font-medium">Reason</label>
          <select
            className={`w-full rounded-xl p-2 text-sm ${compactInputClass}`}
            value={reason}
            onChange={(e) => setReason(e.target.value as any)}
          >
            <option value="no_hits">no_hits</option>
            <option value="low_confidence">low_confidence</option>
            <option value="thumbs_down">thumbs_down</option>
            <option value="error">error</option>
          </select>
        </div>

        <div className="flex-1">
          <label className="text-sm font-medium">Extra tags (comma-separated)</label>
          <input
            className={`w-full rounded-xl p-2 text-sm ${compactInputClass}`}
            value={extraTags}
            onChange={(e) => setExtraTags(e.target.value)}
            placeholder="dev, marketing, pricing"
          />
        </div>

        <button
          className={`rounded-xl px-4 py-2 text-sm font-semibold ${
            canSubmit
              ? "bg-black text-white"
              : "bg-gray-200 text-gray-500 cursor-not-allowed"
          }`}
          disabled={!canSubmit}
          onClick={onLogGap}
        >
          Log KB Gap
        </button>
      </div>
      {savedToast && (
        <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-xl p-2">
          {savedToast}
        </div>
      )}
    </div>
  );
}
